import { create } from 'zustand';
import type { MeProfileResponseLocal, RoleAssignmentInfoLocal } from '../api/types/MeLocal';
import type { Permission, Scope } from '../api/types/permissions';
import { meApi } from '../api/endpoints/me';
import { SessionExpiredError } from '../api/errors';

export interface AuthUser {
    cognitoSub: string;
    email: string;
    displayName: string;
    phone?: string;
    tenantId: string;
    mfaSetupRequired: boolean;
}

interface AuthState {
    user: AuthUser | null;
    permissions: Set<Permission>;
    roleAssignments: RoleAssignmentInfoLocal[];
    /** DEC-RBAC-027: niveles BYOID del propio tenant (de `/me`). `[]` = BYOID apagado. */
    externalScopeLevels: Scope[];
    /** Primera carga en vuelo: todavía NO hay perfil que mostrar. Nunca se prende revalidando. */
    loading: boolean;
    /** Relectura de fondo con el perfil ya en memoria. La pantalla montada se queda como está. */
    revalidating: boolean;
    /** El perfil no se pudo leer y no fue por sesión vencida. Sin esto, los permisos vacíos por una
     *  caída de red se leían como «tu cuenta no tiene acceso». */
    error: Error | null;
    lastLoadedAt: number | null;
    ensureLoaded: () => Promise<AuthState>;
    refresh: () => Promise<void>;
    clear: () => void;
    setFromProfile: (profile: MeProfileResponseLocal) => void;
}

let inflightLoad: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    permissions: new Set(),
    roleAssignments: [],
    externalScopeLevels: [],
    loading: false,
    revalidating: false,
    error: null,
    lastLoadedAt: null,

    // DEC-003: GET /me/profile inline como single source of truth (sin polling). Ver DECISIONES.md.
    ensureLoaded: async () => {
        const state = get();
        if (state.user && state.lastLoadedAt && Date.now() - state.lastLoadedAt < 30_000) {
            return state;
        }
        inflightLoad ??= get()
            .refresh()
            .finally(() => {
                inflightLoad = null;
            });
        await inflightLoad;
        return get();
    },

    refresh: async () => {
        // Con perfil en memoria la relectura es de fondo: ni loader de pantalla completa ni panel de
        // error, porque hay contenido válido que mostrar mientras tanto.
        const isRevalidation = get().user !== null;
        set(isRevalidation ? { revalidating: true } : { loading: true, error: null });
        try {
            const profile = await meApi.getProfile();
            get().setFromProfile(profile);
        } catch (err) {
            if (err instanceof SessionExpiredError) get().clear();
            else if (!isRevalidation) set({ error: asError(err) });
        } finally {
            set({ loading: false, revalidating: false });
        }
    },

    clear: () => {
        set({
            user: null,
            permissions: new Set(),
            roleAssignments: [],
            externalScopeLevels: [],
            error: null,
            lastLoadedAt: null,
        });
    },

    setFromProfile: (profile) => {
        set({
            user: {
                cognitoSub: profile.cognitoSub,
                email: profile.email,
                displayName: profile.displayName,
                phone: profile.phone,
                tenantId: profile.tenantId,
                mfaSetupRequired: profile.mfaSetupRequired,
            },
            permissions: new Set(profile.permissions),
            roleAssignments: profile.roleAssignments,
            externalScopeLevels: profile.externalScopeLevels,
            error: null,
            lastLoadedAt: Date.now(),
        });
    },
}));

/** El error de la lectura llega tipado laxo; el store necesita un `Error`. */
function asError(error: unknown): Error {
    return error instanceof Error ? error : new Error('No se pudo leer el perfil');
}

/** Internal test hook — fuerza el reset del inflight lock entre tests. */
export const _resetInflightForTests = (): void => {
    inflightLoad = null;
};
